// 加工指示の追加ダイアログ状態と、選択エントリの読込/サンプル表示連携をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiAddDialogService(deps) {
    const {
        DeepCopy,
        DicVal,
        GridData,
        KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID,
        KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS,
        KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES,
        activeKakousijiKeyRef,
        activeKakousijiSampleGroupEntryKey,
        appendKakousijiDraft,
        applyActiveKakousijiSelectionData,
        applyBrandHinbanNumberingLockToFields,
        applyKakousijiSection139Detail,
        buildKakousijiSampleGroupSummaryItem,
        buildKakousijiSection139SaveRow,
        clearActiveKakousijiSelectionData,
        columninfo,
        filterBaseFields,
        fnc,
        getKakousijiEntryCache,
        getKakousijiEntryCacheForSummaryItem,
        getKakousijiKubunOptions,
        gridlist,
        isEmpty,
        isKakousijiSampleGroupSummaryItem,
        kakousijiAddDialogState,
        kakousijiAmountRoundingRef,
        kakousijiLoadRequestSeqRef,
        kakousijiLoadingRef,
        kakousijiSampleFieldSource,
        kakousijiSampleGroupEntryKeys,
        lastLoadedKakousijiCodeRef,
        normalizeKakousijiSampleDetailForStoreByType,
        panel,
        reserveKakousijiSequence,
        resolveCurrentOppiCode,
        resolveInitialProductionKakousijiItem,
        setKakousijiAddDialogState,
        setKakousijiEntryCache,
        setKakousijiSummaryList,
        snapshotActiveKakousijiSelectionData,
        uniqueFieldsByColumnId
    } = deps;

    function openKakousijiAddDialog() {
        const oppiCode = resolveCurrentOppiCode();
        if (isEmpty(oppiCode)) {
            fnc.current.warnmsg("先に品番を登録してから加工指示を追加してください");
            return;
        }
        const options = getKakousijiKubunOptions();
        const initialOption = options.find((item) => String(DicVal(item, "code", "")).trim() === "2") || DicVal(options, 0, {});
        setKakousijiAddDialogState({
            open: true,
            options: options,
            selectedValue: String(DicVal(initialOption, "code", "")).trim(),
            busy: false
        });
    }
    function commitKakousijiAddDialog() {
        if (kakousijiAddDialogState.busy) return;
        const options = Array.isArray(kakousijiAddDialogState.options) ? kakousijiAddDialogState.options : [];
        const selected = options.find((item) =>
            String(DicVal(item, "code", "")).trim() === String(kakousijiAddDialogState.selectedValue ?? "").trim()
        ) || DicVal(options, 0, null);
        if (isEmpty(selected)) return;

        snapshotActiveKakousijiSelectionData();
        setKakousijiAddDialogState((prev) => ({ ...prev, busy: true }));

        const finalizeDraft = (sourceSnapshot) => {
            reserveKakousijiSequence(
                (ssdNo) => {
                    appendKakousijiDraft(selected, sourceSnapshot, ssdNo);
                    setKakousijiAddDialogState((prev) => ({ ...prev, open: false, busy: false }));
                },
                () => setKakousijiAddDialogState((prev) => ({ ...prev, busy: false }))
            );
        };

        const label = String(DicVal(selected, "name", "")).trim();
        if (label.indexOf("追加") === -1) {
            finalizeDraft(null);
            return;
        }

        const sourceItem = resolveInitialProductionKakousijiItem();
        if (isEmpty(sourceItem)) {
            setKakousijiAddDialogState((prev) => ({ ...prev, busy: false }));
            fnc.current.warnmsg("量産の加工指示データが存在しないため、追加データを作成できません。");
            return;
        }

        const cached = getKakousijiEntryCacheForSummaryItem(sourceItem);
        if (!isEmpty(cached)) {
            finalizeDraft(cached);
            return;
        }

        fnc.current.postaxios("kakousiji/GetEntryData", { code: sourceItem.code, colinfo: columninfo, controller: "kakousiji" },
            {
                onSuccess: function (data) {
                    // 金額の端数処理設定は明細を反映する前に確定させる。
                    kakousijiAmountRoundingRef.current = DicVal(data, "amountrounding", kakousijiAmountRoundingRef.current);
                    finalizeDraft({
                        data: DicVal(data, "data", {}),
                        detail: DicVal(data, "detail", {})
                    });
                },
                onError: function (response) {
                    setKakousijiAddDialogState((prev) => ({ ...prev, busy: false }));
                    let msg = "加工指示データの取得に失敗しました";
                    if (!isEmpty(response.errDescription)) msg = response.errDescription;
                    fnc.current.warnmsg(msg);
                }
            }, true
        );
    }
    function closeKakousijiAddDialog() {
        setKakousijiAddDialogState((prev) => ({
            ...prev,
            open: false,
            busy: false
        }));
    }
    function loadActiveKakousijiSelectionData(summaryItem) {
        const code = String(DicVal(summaryItem, "code", "")).trim();
        const key = String(DicVal(summaryItem, "key", "")).trim();
        const loadIdentity = key !== "" ? key : code;
        if (lastLoadedKakousijiCodeRef.current === loadIdentity) {
            return;
        }
        const cache = getKakousijiEntryCacheForSummaryItem(summaryItem);
        if (!isEmpty(cache)) {
            lastLoadedKakousijiCodeRef.current = loadIdentity;
            applyActiveKakousijiSelectionData(DicVal(cache, "data", {}), DicVal(cache, "detail", {}), DicVal(cache, "orderHistory", {}));
            return;
        }
        if (code === "") {
            lastLoadedKakousijiCodeRef.current = "";
            clearActiveKakousijiSelectionData();
            return;
        }
        clearActiveKakousijiSelectionData();
        const requestSeq = kakousijiLoadRequestSeqRef.current + 1;
        kakousijiLoadRequestSeqRef.current = requestSeq;
        kakousijiLoadingRef.current = true;
        fnc.current.postaxios("kakousiji/GetEntryData", { code: code, colinfo: columninfo, controller: "kakousiji" },
            {
                onSuccess: function (data) {
                    // 金額の端数処理設定は明細を反映する前に確定させる。
                    kakousijiAmountRoundingRef.current = DicVal(data, "amountrounding", kakousijiAmountRoundingRef.current);
                    const snapshot = { data: DicVal(data, "data", {}), detail: DicVal(data, "detail", {}) };
                    const isLatestRequest = kakousijiLoadRequestSeqRef.current === requestSeq;
                    const isActiveRequest = String(activeKakousijiKeyRef.current ?? "").trim() === loadIdentity;
                    lastLoadedKakousijiCodeRef.current = loadIdentity;
                    setKakousijiEntryCache(DicVal(summaryItem, "key", code), snapshot);
                    setKakousijiEntryCache(code, snapshot);
                    if (isLatestRequest && isActiveRequest) {
                        applyActiveKakousijiSelectionData(snapshot.data, snapshot.detail);
                    }
                    if (isLatestRequest) kakousijiLoadingRef.current = false;
                },
                onError: function (data) {
                    if (kakousijiLoadRequestSeqRef.current === requestSeq) kakousijiLoadingRef.current = false;
                    let msg = "加工指示データの取得に失敗しました";
                    if (!isEmpty(data.errDescription)) msg = data.errDescription;
                    fnc.current.warnmsg(msg);
                }
            }, true
        );
    }
    function snapshotKakousijiSampleGroupDisplayEntry(groupItem, entryKey, entryIndex) {
        const currentCache = getKakousijiEntryCache(entryKey);
        if (isEmpty(currentCache)) return null;
        const data = DeepCopy(DicVal(currentCache, 'data', {}));
        const detail = DeepCopy(DicVal(currentCache, 'detail', {}));
        KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES.forEach((type) => {
            const sourcePanel = (Array.isArray(gridlist) ? gridlist : []).find((item) => String(DicVal(item, 'type', '')).trim() === type)
                || (Array.isArray(panel) ? panel : []).find((item) => String(DicVal(item, 'type', '')).trim() === type);
            if (isEmpty(sourcePanel)) return;
            const displayPanel = {
                ...sourcePanel,
                id: `${DicVal(sourcePanel, 'id', '')}_sample_${entryIndex}`,
                sourcePanelId: DicVal(sourcePanel, 'id', ''),
                sampleEntryKey: entryKey,
                sampleEntryIndex: entryIndex,
                sampleDisplayKey: `sample_${entryIndex}_${DicVal(sourcePanel, 'id', '')}_${type}`
            };
            if (String(type).trim() === '139') {
                const baseRef = kakousijiSampleDisplayEntryDataRef(displayPanel);
                Object.assign(data, DeepCopy(DicVal(baseRef, 'current', {})));
                detail['139'] = [buildKakousijiSection139SaveRow(data)];
                return;
            }
            const rows = GridData(displayPanel);
            if (!Array.isArray(rows)) return;
            detail[type] = DeepCopy(normalizeKakousijiSampleDetailForStoreByType(type, rows));
        });
        const snapshot = { data: data, detail: detail };
        setKakousijiEntryCache(entryKey, snapshot);
        return snapshot;
    }
    function refreshKakousijiSampleGroupSummaryFromCache(targetEntryKey = '') {
        const targetKey = String(targetEntryKey ?? '').trim();
        setKakousijiSummaryList((prev) => (Array.isArray(prev) ? prev : []).map((item, index) => {
            if (!isKakousijiSampleGroupSummaryItem(item)) return item;
            const keys = kakousijiSampleGroupEntryKeys(item);
            if (targetKey !== '' && keys.length !== 0 && keys[0] !== targetKey) return item;
            return buildKakousijiSampleGroupSummaryItem(item, keys, activeKakousijiSampleGroupEntryKey(item), index);
        }));
    }
    function kakousijiSampleUpperFields(panelInfo, fallbackFields) {
        const sourceFields = Array.isArray(fallbackFields) && fallbackFields.length !== 0
            ? fallbackFields
            : kakousijiSampleFieldSource(panelInfo);
        return uniqueFieldsByColumnId(applyBrandHinbanNumberingLockToFields(filterBaseFields(sourceFields, panelInfo)))
            .filter((field) => ![KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID, ...KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS].includes(String(DicVal(field, 'columnid', DicVal(field, 'field', ''))).trim().toUpperCase()));
    }
    function kakousijiSampleDisplayEntryDataRef(panelInfo) {
        const entryKey = String(DicVal(panelInfo, 'sampleEntryKey', '')).trim();
        const snapshot = getKakousijiEntryCache(entryKey);
        return { current: DeepCopy(DicVal(snapshot, 'data', {})) };
    }
    function handleKakousijiSampleDisplayBaseValueChanged(panelInfo, id, value, edit, options) {
        const entryKey = String(DicVal(panelInfo, 'sampleEntryKey', '')).trim();
        if (entryKey === '') return;
        const snapshot = getKakousijiEntryCache(entryKey) || { data: {}, detail: {} };
        const next = {
            data: DeepCopy(DicVal(snapshot, 'data', {})),
            detail: DeepCopy(DicVal(snapshot, 'detail', {}))
        };
        next.data[id] = value;
        next.detail = applyKakousijiSection139Detail(next.detail, next.data);
        setKakousijiEntryCache(entryKey, next);
        if (String(id ?? '').trim().toUpperCase() === 'SSD_SIIRECD') refreshKakousijiSampleGroupSummaryFromCache(entryKey);
        if (!isEmpty(fnc.current) && typeof fnc.current.modify === 'function') fnc.current.modify(true);
    }

    return {
        openKakousijiAddDialog,
        commitKakousijiAddDialog,
        closeKakousijiAddDialog,
        loadActiveKakousijiSelectionData,
        snapshotKakousijiSampleGroupDisplayEntry,
        refreshKakousijiSampleGroupSummaryFromCache,
        kakousijiSampleUpperFields,
        kakousijiSampleDisplayEntryDataRef,
        handleKakousijiSampleDisplayBaseValueChanged
    };
}
